import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import * as ErrorReporting from 'services/error-reporting/error-reporting'
import { Skeleton } from 'components/Skeleton'
import { useAnalytics } from 'hooks'
import { cn } from 'lib/utils'
import { Loader2, Plus } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { WeeklyQueueItem, WeeklyQueuePendingAction } from '../../store/weekly-queue-store'
import { useWeeklyQueueStore } from '../../store/weekly-queue-store'
import { PostSkeleton, QueuedPostCard } from './QueuedPostCard'

const CARD_WIDTH = 'w-[240px]'
// Matches a populated 240px-wide QueuedPostCard so the row doesn't shift height
// when a day flips between empty and populated.
const CARD_MIN_HEIGHT = 'min-h-[316px]'

interface DayDetailProps {
  date: string | null
  dayOffset: number | null
  items: WeeklyQueueItem[]
  /** Client-side "Add a post" placeholders; not driven by server expectedCount. */
  optimisticDraftCount: number
  /**
   * Server's day bucket `expectedCount` — autopilot has scheduled posts for
   * this day that haven't materialized as creatives yet. We render the gap
   * (`expectedCount - items.length`) as drafting skeletons so the day detail
   * matches the strip's purple "expected" dot instead of looking empty while
   * we poll.
   */
  expectedCount: number
  pendingActions: Record<string, WeeklyQueuePendingAction>
  /** Creative whose PostModal payload is currently loading (per-card veil). */
  openingId: string | null
  onDelete: (creativeId: string, channel: string) => void
  /** Whole-card click → open PostModal. */
  onOpenPost: (creativeId: string) => void
  /** ••• Edit → composer. */
  onEdit: (creativeId: string, channel: string) => void
  /** ••• Unschedule → move a scheduled post back to a draft. */
  onUnschedule: (creativeId: string) => void
  onApprove: (creativeId: string) => void
}

function formatDayHeading(iso: string, offset: number): string {
  const d = new Date(iso)
  const dateStr = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
  if (offset === 0) return `Today, ${dateStr}`
  if (offset === 1) return `Tomorrow, ${dateStr}`
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

const STATUS_PRIORITY: Record<string, number> = {
  generating: 0,
  needs_approval: 1,
  scheduled: 2,
  published: 3,
  failed: 4,
}

function sortItems(items: WeeklyQueueItem[]): WeeklyQueueItem[] {
  return [...items].sort((a, b) => {
    const pa = STATUS_PRIORITY[a.status] ?? 9
    const pb = STATUS_PRIORITY[b.status] ?? 9
    if (pa !== pb) return pa - pb
    const ta = a.scheduledAt ? new Date(a.scheduledAt).getTime() : Number.MAX_SAFE_INTEGER
    const tb = b.scheduledAt ? new Date(b.scheduledAt).getTime() : Number.MAX_SAFE_INTEGER
    return ta - tb
  })
}

export function DayDetail({
  date,
  dayOffset,
  items,
  optimisticDraftCount,
  expectedCount,
  pendingActions,
  openingId,
  onDelete,
  onOpenPost,
  onEdit,
  onUnschedule,
  onApprove,
}: DayDetailProps) {
  const addPostForDay = useWeeklyQueueStore(s => s.addPostForDay)
  const { track } = useAnalytics()
  const [isAdding, setIsAdding] = useState(false)
  // Sync guard so a second click in the same tick can't fire before React
  // flushes the disabled state on the button.
  const inFlightRef = useRef(false)
  const cardsRowRef = useRef<HTMLDivElement | null>(null)
  const sorted = useMemo(() => sortItems(items), [items])

  // Keyboard loop. Cards inside the row carry tabIndex + a
  // `data-card-index` attribute; this handler intercepts j/k/← /→ to
  // move card focus, Enter/Space + E to open the editor, and S to delete
  // a scheduled post. Bails on INPUT/TEXTAREA so composer
  // fields aren't intercepted. Hook lives above the early return so
  // React's hook-order rule stays satisfied.
  const handleCardKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const focused = document.activeElement as HTMLElement | null
      if (!focused) return
      if (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA') return

      const card = focused.closest<HTMLElement>('[data-card-index]')
      if (!card) return
      const focusedIndex = parseInt(card.dataset.cardIndex ?? '-1', 10)
      if (focusedIndex < 0 || focusedIndex >= sorted.length) return

      const focusCardAt = (i: number) => {
        cardsRowRef.current?.querySelector<HTMLElement>(`[data-card-index="${i}"]`)?.focus()
      }

      const item = sorted[focusedIndex]
      if (!item) return

      switch (e.key) {
        case 'j':
        case 'ArrowRight':
          e.preventDefault()
          focusCardAt(Math.min(focusedIndex + 1, sorted.length - 1))
          return
        case 'k':
        case 'ArrowLeft':
          e.preventDefault()
          focusCardAt(Math.max(focusedIndex - 1, 0))
          return
        case 'Enter':
        case ' ': {
          // Only intercept Enter/Space when the article itself is the
          // focused element. If a button inside the card is focused
          // (e.g. Approve, More options), let the browser handle its
          // native click activation — preventing default would
          // silently break the buttons.
          if (focused.tagName !== 'ARTICLE') return
          e.preventDefault()
          onOpenPost(item.creativeId)
          return
        }
        case 'e':
        case 'E':
          e.preventDefault()
          onEdit(item.creativeId, item.channel)
          return
        case 's':
        case 'S':
          if (item.status !== 'scheduled') return
          e.preventDefault()
          onDelete(item.creativeId, item.channel)
          return
      }
    },
    [sorted, onOpenPost, onEdit, onDelete]
  )

  if (!date || dayOffset == null) return null

  // Two independent sources of placeholders:
  //   - `optimisticDraftCount`: client-only "Add a post" clicks awaiting the
  //     POST response. Decoupled from server state by design.
  //   - autopilotExpected: server told us (`expectedCount`) it has more drafts
  //     coming for this day than we currently have items for. Without this the
  //     strip shows a purple dot but the day detail looks empty until a
  //     manual refresh. Polling will replace these with real cards as the
  //     server completes generation.
  const autopilotExpectedPlaceholders = Math.max(0, expectedCount - items.length)
  const placeholderCount = Math.max(0, optimisticDraftCount) + autopilotExpectedPlaceholders
  const heading = formatDayHeading(date, dayOffset)

  const handleAddPost = async () => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    setIsAdding(true)
    track(AnalyticsEvents.Autopilot.WEEKLY_ADDPOST_REQUESTED, {
      date,
      day_offset: dayOffset,
    })
    try {
      await addPostForDay(date)
    } catch (error) {
      ErrorReporting.captureException(error, {
        tags: { feature: 'weekly_cockpit', step: 'add_post' },
        extra: { date, dayOffset },
      })
      console.error('Add-post failed:', error)
    } finally {
      inFlightRef.current = false
      setIsAdding(false)
    }
  }

  const empty = sorted.length === 0 && placeholderCount === 0

  // Progress signal: of the posts on the rails this day (scheduled / generating
  // / published), how many have actually gone live? Failed posts sit outside
  // the count — they're not progress.
  const decidedCount = sorted.filter(it => it.status === 'published').length
  const relevantCount = sorted.filter(
    it => it.status === 'scheduled' || it.status === 'generating' || it.status === 'published'
  ).length
  const showProgress = relevantCount > 0
  const progressRatio = showProgress ? decidedCount / relevantCount : 0

  return (
    <div className="mt-5">
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        <h3 className="min-w-0 truncate text-[14px] font-semibold tracking-[-0.005em] text-paper">
          {heading}
        </h3>
        {showProgress && (
          <div className="flex items-center gap-2" aria-live="polite">
            <span className="mono text-[10px] tracking-[0.18em] text-paper-faint uppercase">
              {decidedCount} of {relevantCount} done
            </span>
            <div className="relative h-px w-24 overflow-hidden bg-line" aria-hidden>
              <div
                className="absolute inset-y-0 left-0 bg-acid transition-[width] duration-300 ease-out"
                style={{ width: `${progressRatio * 100}%` }}
              />
            </div>
          </div>
        )}
        {!empty && (
          <button
            type="button"
            onClick={handleAddPost}
            disabled={isAdding}
            className="ml-auto inline-flex h-7 items-center gap-1.5 rounded-[6px] border border-line-bright bg-bg-2 px-2.5 text-[11px] font-medium text-paper-dim transition-colors hover:border-paper-dim hover:bg-bg-3 hover:text-paper disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Plus size={12} />
            Add a post
          </button>
        )}
      </div>

      <div className="mb-3 h-px bg-line" />

      <div
        ref={cardsRowRef}
        onKeyDown={handleCardKeyDown}
        className={cn(
          '-mx-1 flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto px-1 pb-2',
          CARD_MIN_HEIGHT
        )}
        style={{ scrollbarWidth: 'thin' }}
      >
        {sorted.map((item, i) => (
          // Every queue item renders through the canonical QueuedPostCard.
          // The card is width-agnostic, so the snap-row width lives on this
          // wrapper. Whole-card click opens PostModal; ••• carries Edit/Delete;
          // review-mode posts get an inline Approve.
          <div key={item.creativeId} className={cn('shrink-0 snap-start', CARD_WIDTH)}>
            <QueuedPostCard
              cardIndex={i}
              item={item}
              pendingAction={pendingActions[item.creativeId]}
              opening={openingId === item.creativeId}
              onOpenPost={() => onOpenPost(item.creativeId)}
              onEdit={() => onEdit(item.creativeId, item.channel)}
              onUnschedule={() => onUnschedule(item.creativeId)}
              onDelete={() => onDelete(item.creativeId, item.channel)}
              onApprove={() => onApprove(item.creativeId)}
            />
          </div>
        ))}

        {Array.from({ length: placeholderCount }).map((_, i) => (
          <DraftingPlaceholderCard key={`placeholder-${i}`} />
        ))}

        {/* The dashed-tile AddPostCard is the focal CTA when the day is
            empty. On loaded days the inline ghost button in the heading
            row is the affordance, so the tile is hidden to keep the
            scroll-row from pushing it off-screen on busy days. */}
        {empty && (
          <AddPostCard
            onClick={handleAddPost}
            disabled={isAdding}
            tagline="Nothing planned. Add one."
          />
        )}
      </div>
    </div>
  )
}

/**
 * Optimistic placeholder shaped exactly like a generating QueuedPostCard, so the
 * swap when the real creative arrives is visually invisible.
 */
function DraftingPlaceholderCard() {
  return (
    <article
      className={cn(
        'relative flex shrink-0 snap-start flex-col overflow-hidden rounded-[12px] border border-line bg-bg-2',
        CARD_WIDTH
      )}
    >
      <div className="flex items-center gap-1.5 border-b border-line px-2.5 py-1.5 text-[11px]">
        <Skeleton variant="dark" className="h-3 w-3 shrink-0 rounded-sm" />
        <Skeleton variant="dark" className="h-2.5 w-16 rounded" />
        <span className="bg-acid-tint mono ml-auto inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] whitespace-nowrap text-acid">
          <Loader2 size={10} className="animate-spin" />
          Drafting
        </span>
      </div>
      <PostSkeleton />
      <div className="mt-auto h-9 border-t border-line bg-bg-3" />
    </article>
  )
}

function AddPostCard({
  onClick,
  disabled,
  tagline,
}: {
  onClick: () => void
  disabled?: boolean
  tagline: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex shrink-0 snap-start flex-col items-center justify-center gap-1.5 rounded-[12px] border-2 border-dashed border-line px-3 text-paper-faint transition-colors',
        'hover:border-acid hover:bg-[color:var(--acid-tint)] hover:text-acid',
        'disabled:cursor-not-allowed disabled:opacity-70',
        CARD_WIDTH,
        CARD_MIN_HEIGHT
      )}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-full border border-line-bright bg-bg-3">
        <Plus size={16} />
      </div>
      <span className="text-[12px] font-semibold text-paper">Add a post</span>
      <span className="text-center text-[10px] text-paper-faint">{tagline}</span>
    </button>
  )
}
